import {ChangeDetectionStrategy, Component, Injectable, input, Signal} from "@angular/core";
import {MøteId} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {combineLatest, interval, Observable, startWith, switchMap} from "rxjs";
import {toObservable, toSignal} from "@angular/core/rxjs-interop";
import {Temporal} from "temporal-polyfill";
import {NgClass} from "@angular/common";
import {TranslateModule} from "@ngx-translate/core";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {map} from "rxjs/operators";
import {durationTilMinutter} from "felles-ng2/støttefunksjoner/temporal";
import {første} from "felles-ng2/støttefunksjoner/lister";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {Sakstidspunkter, SakstidspunkterInformasjon} from "felles-ng2/tjenester/Sakstidspunkter";
import {AgendaelementModellDLM, SakDatatjeneste} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import ZonedDateTime = Temporal.ZonedDateTime;

const debug = initDebugger('SaksprogresjonKomponent');

/**
 * Sirkulære progresjonsindikatorer som viser fremdrift for aktiv sak og totalt møte
 */
@Component({
    selector: 'saksprogresjon',
    templateUrl: 'saksprogresjon.html',
    styleUrl: 'saksprogresjon.scss',
    imports: [
        NgClass,
        TranslateModule,
        MatProgressSpinner
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SaksprogresjonKomponent {
    static ajsSelector = 'saksprogresjon';

    moeteId = input.required<MøteId>();

    progresjon: Signal<Progresjonsinformasjon>;

    constructor(private avhengigheter: SaksprogresjonKomponentAvhengigheter,
                private sakstidspunkter: Sakstidspunkter,
                private klokke: Klokke,
                private oversettelser:Oversettelser) {
        this.progresjon = toSignal(toObservable(this.moeteId).pipe(
          switchMap(møteId => combineLatest([
              this.avhengigheter.saker(møteId),
              this.sakstidspunkter.forMøteSomObserverbar(møteId),
              this.hvertNSekund(30)
          ])),
          map(([saker, sakstidspunkter]) => this.beregnProgresjon(saker, sakstidspunkter))
        ), {initialValue: tomProgresjonsinformasjon()});
    }

    // Metoder for template

    formaterTid(minutter: number): string {
        if (minutter <= 60) return `${Math.round(minutter)}`;
        const timer = Math.floor(minutter / 60);
        const minutterStr = String(minutter % 60).padStart(2, '0');
        return `${timer}:${minutterStr}`;
    }

    tidsbenevnelse(minutter: number): string {
        return minutter > 60 ? this.oversettelser.oversett(i18n('t igjen')) : this.oversettelser.oversett(i18n('m igjen'));
    }

    //private hjelpemetoder

    private beregnProgresjon(saker: AgendaelementModellDLM[], sakstidspunkter: SakstidspunkterInformasjon): Progresjonsinformasjon {
        const nå = this.klokke.nåSomZonedDateTime();

        // Sjekk om vi skal vise progresjon
        if (!sakstidspunkter.harSakSomBehandlesNåPlussMinusEnTime(nå))
            return tomProgresjonsinformasjon();

        // Beregn møtets totale varighet og forbrukt tid for dagen
        const møtestart = sakstidspunkter.sakerPåDagStart(nå);
        const møtevarighet = durationTilMinutter(sakstidspunkter.sakerPåDagVarighet(nå));
        const møtetidBrukt = durationTilMinutter(møtestart.until(nå));
        const møtetidRest = møtevarighet - møtetidBrukt;
        const møteprogresjonIProsent = (møtetidBrukt / møtevarighet) * 100;

        // Finn aktiv sak basert på sakstidspunkter
        const aktivSak = this.finnAktivSak(saker, sakstidspunkter, nå);
        return {
            visProgresjonsindikator: true,
            moetettidBrukt: møtetidBrukt,
            moetettidRest: møtetidRest,
            moeteprogresjonIProsent: møteprogresjonIProsent,
            aktivSak: aktivSak
        };
    }

    private finnAktivSak(saker: AgendaelementModellDLM[], sakstidspunkter: SakstidspunkterInformasjon, nå: ZonedDateTime): Saksprogresjon | null {
        return første(sakstidspunkter.sakerPåDag(nå)
          .filter(sakstidspunkt => sakstidspunkt.inneholder(nå))
          .map(sakstidspunkt => ({sakstidspunkt, sak: saker.find(s => s.id === sakstidspunkt.sakId)}))
          .filter(tidspunktOgSak => erIkkeNil(tidspunktOgSak.sak))
          .map(tidspunktOgSak => {
              const tidBrukt = durationTilMinutter(tidspunktOgSak.sakstidspunkt.start!.until(nå));
              const varighet = durationTilMinutter(tidspunktOgSak.sakstidspunkt.varighet!);
              const progresjon = (tidBrukt / varighet) * 100;
              const rest = Math.round(varighet * (1 - (progresjon / 100)));
              return {
                  sakId: tidspunktOgSak.sak!.id,
                  sakNummer: tidspunktOgSak.sak!.nummer,
                  erUnderBehandling: true,
                  progresjon,
                  varighet,
                  rest
              };
          }));
    }
    /** Leverer verdi hvert N-te sekund */
    private hvertNSekund(intervalISekunder: number): Observable<number> {
        return interval(1000 * intervalISekunder).pipe(startWith(0));
    }
}

@Injectable()
export class SaksprogresjonKomponentAvhengigheter {

    constructor(private sakDatatjeneste: SakDatatjeneste) {
    }

    saker(id: MøteId): Observable<AgendaelementModellDLM[]> {
        return this.sakDatatjeneste.finnTilknyttetMøteFraLokallager(id);
    }
}

interface Progresjonsinformasjon {
    visProgresjonsindikator: boolean;
    moetettidBrukt: number;
    moetettidRest: number;
    moeteprogresjonIProsent: number;
    aktivSak: Saksprogresjon | null;
}

interface Saksprogresjon {
    sakId: string;
    sakNummer: string;
    erUnderBehandling: boolean;
    progresjon: number;
    varighet: number;
    rest: number;
}

function tomProgresjonsinformasjon(): Progresjonsinformasjon {
    return {
        visProgresjonsindikator: false,
        moetettidBrukt: 0,
        moetettidRest: 0,
        moeteprogresjonIProsent: 0,
        aktivSak: null
    }
}
